import React, { useEffect, useMemo } from 'react';
import { isClient } from '../../common/utils';
import { logger } from '../../common/logger';
import { loadBraze } from './loadBraze';
import { fetchBrazeToken } from '../../service/user-profile-api';
import { brazeBaseUrl } from './constants';
import { isDevelopmentEnvironment } from '../utils/env-utils';
import { InAppMessage } from '../../client/braze/types';
import { Language } from '@yleisradio/areena-types';
import { loginFetchClient as getLoginFetch } from '../../service/loginFetchClient';
import { BrazeContext } from './BrazeContext';
import { yleConsentSdkHeadless } from '@yleisradio/consent-sdk/headless';
import { useAuthContext } from '../contexts/AuthContext/AuthContext';
import { inAppMessageValidator } from './InAppMessage/utils';
import { useFeatureFlag } from '../contexts/FeatureFlagContext/FeatureFlagContext';
import { useAppFeaturesContext } from '../contexts/AppFeaturesContext/AppFeaturesContext';

const BrazeProviderComponent: React.FC<React.PropsWithChildren<Props>> = ({
  children,
  language,
}) => {
  const { brazeEnabled } = useFeatureFlag();
  const { brazeApiKey } = useAppFeaturesContext();
  const { user } = useAuthContext();
  const isTargetingConsentGiven = isClient()
    ? yleConsentSdkHeadless.getConsent().targeting
    : false;

  const isBrazeAllowed =
    isClient() &&
    !!user &&
    !user.isSubAccount &&
    isTargetingConsentGiven &&
    brazeEnabled;

  const [isBrazeLoaded, setIsBrazeLoaded] = React.useState(false);
  const [inAppMessages, setInAppMessages] = React.useState<InAppMessage[]>([]);

  const removeMessage = (msg: InAppMessage) => {
    setInAppMessages(messages =>
      messages.filter(message => message.triggerId !== msg.triggerId)
    );

    const braze = isClient() ? window.braze : undefined;
    if (!braze) {
      return;
    }
  };

  useEffect(() => {
    if (!isBrazeAllowed || isBrazeLoaded) {
      return;
    }

    logger.debug('Loading Braze.');

    loadBraze(() => setIsBrazeLoaded(true));
  }, [isBrazeAllowed, isBrazeLoaded]);

  const loginFetch = useMemo(() => getLoginFetch(), []);

  useEffect(() => {
    const braze = isClient() ? window.braze : undefined;
    if (!isBrazeLoaded || !braze || !isBrazeAllowed) {
      return;
    }

    logger.debug('Starting braze initialization');

    if (brazeApiKey === undefined) {
      logger.error('No Braze API key found. Cancelling Braze initialization.');
      return;
    }

    braze.initialize(brazeApiKey, {
      baseUrl: brazeBaseUrl,
      disablePushTokenMaintenance: true,
      manageServiceWorkerExternally: true,
      enableLogging: isDevelopmentEnvironment(window.location.hostname),
      localization: language,
      enableSdkAuthentication: true,
    });

    if (user?.userId.length) {
      fetchBrazeToken(loginFetch)
        .then(brazeToken => {
          braze.changeUser(user.userId, brazeToken);
          braze.getUser()?.setLanguage(language);

          braze.openSession();
        })
        .catch(err => {
          logger.error(
            'Fetching braze token failed. Opening anonymous Braze session.',
            err
          );
          braze.openSession();
        });
    } else {
      logger.error('No userId found. Opening anonymous Braze session.');
      braze.openSession();
    }

    return () => {
      logger.debug('Terminating Braze.');
      braze.wipeData();
      braze.destroy();
    };
  }, [brazeApiKey, isBrazeLoaded, isBrazeAllowed, language, user, loginFetch]);

  useEffect(() => {
    const braze = isClient() ? window.braze : undefined;

    if (!isBrazeLoaded || !braze) {
      return;
    }

    logger.debug('Setting up Braze subscriptions');

    braze.subscribeToSdkAuthenticationFailures(() => {
      logger.debug('Refreshing braze token.');

      fetchBrazeToken(loginFetch)
        .then(brazeToken => {
          braze.setSdkAuthenticationSignature(brazeToken);
        })
        .catch(err => {
          logger.error('Refreshing braze token failed.', err);
        });
    });
  }, [isBrazeLoaded, loginFetch]);

  useEffect(() => {
    const braze = isClient() ? window.braze : undefined;

    if (!isBrazeLoaded || !braze) {
      return;
    }

    logger.debug('Setting up Braze in-app message subscription');

    braze.subscribeToInAppMessage((message: InAppMessage) => {
      const messageOK = inAppMessageValidator(message);

      if (messageOK) {
        setInAppMessages(messages => [...messages, message]);
      }
    });
  }, [isBrazeLoaded]);

  const inAppMessageClick = (message: InAppMessage) => {
    const braze = isClient() ? window.braze : undefined;
    if (!braze) {
      return;
    }
    braze.logInAppMessageClick(message);
  };

  return (
    <BrazeContext.Provider
      value={{
        isBrazeInitialized: isClient() && window.braze !== undefined,
        inAppMessages,
        inAppMessageClick,
        removeMessage,
      }}
    >
      {children}
    </BrazeContext.Provider>
  );
};

type Props = {
  language: Language;
};

export const BrazeProvider = BrazeProviderComponent;
